import { MessageCodeEnum } from '~/constants/MessageCode';
import type { ValidationResult } from '~/types/common/validationResult';

/**
 * バリデーションユーティリティ
 */
export const validationUtils = {

  /**
   * 必須チェック
   *
   * @param value 値
   * @returns バリデーション結果
   */
  checkRequired: (value: string | null | undefined): ValidationResult<typeof MessageCodeEnum.REQUIRED_FIELD> => {
    const valid = value !== null && value !== undefined && value !== '';
    return valid
      ? { valid: true }
      : { valid: false, error: MessageCodeEnum.REQUIRED_FIELD };
  },

  /**
   * 現在のパスワードと新しいパスワードの不一致チェック
   *
   * @param currentPassword 現在のパスワード
   * @param newPassword 新しいパスワード
   * @returns バリデーション結果
   */
  checkPasswordMismatch: (currentPassword: string, newPassword: string): ValidationResult<typeof MessageCodeEnum.PASSWORD_MISMATCH> => {
    const valid = currentPassword !== newPassword;
    return valid
      ? { valid: true }
      : { valid: false, error: MessageCodeEnum.PASSWORD_MISMATCH };
  },

  /**
   * パスワードポリシーチェック
   *
   * 下記ポリシーを満たすかを検証する:
   * - 長さ: 8文字以上16文字以下
   * - 使用可能文字: 印字可能な半角文字（半角英数字・半角記号）のみ。全角文字・絵文字・空白・制御文字は不可
   * - 必須文字種: 英大文字・英小文字・数字・記号（英数字以外の印字可能な半角文字）をすべて含む
   *
   * @param password パスワード
   * @returns バリデーション結果
   */
  checkPasswordPolicy: (password: string): ValidationResult<typeof MessageCodeEnum.PASSWORD_POLICY_VIOLATION> => {
    if (password === '') {
      // 未入力の場合はチェックしない
      return { valid: true };
    }
    // 使用可能文字: 印字可能な半角文字のみ、8文字以上16文字以下
    const allowedCharsRegex = /^[\x21-\x7e]{8,16}$/;
    // 必須文字種（記号 = 印字可能な半角文字のうち英数字以外）
    const hasLowerCaseRegex = /[a-z]/;
    const hasUpperCaseRegex = /[A-Z]/;
    const hasDigitRegex = /[0-9]/;
    const hasSymbolRegex = /[\x21-\x2f\x3a-\x40\x5b-\x60\x7b-\x7e]/;
    const valid = allowedCharsRegex.test(password)
      && hasLowerCaseRegex.test(password)
      && hasUpperCaseRegex.test(password)
      && hasDigitRegex.test(password)
      && hasSymbolRegex.test(password);
    return valid
      ? { valid: true }
      : { valid: false, error: MessageCodeEnum.PASSWORD_POLICY_VIOLATION };
  },

  /**
   * 新しいパスワードと確認用のパスワードの一致チェック
   *
   * @param newPassword 新しいパスワード
   * @param confirmPassword 確認用のパスワード
   * @returns バリデーション結果
   */
  checkNewPasswordMatch: (newPassword: string, confirmPassword: string): ValidationResult<typeof MessageCodeEnum.PASSWORD_CONFIRMATION_MISMATCH> => {
    const valid = newPassword === confirmPassword;
    return valid
      ? { valid: true }
      : { valid: false, error: MessageCodeEnum.PASSWORD_CONFIRMATION_MISMATCH };
  },

  /**
   * 全角/半角カナのみのチェック
   *
   * @param value 値
   * @returns バリデーション結果
   */
  checkKanaOnly: (value: string): ValidationResult<typeof MessageCodeEnum.KANA_ONLY> => {
    if (value === '') {
      // 未入力の場合はチェックしない
      return { valid: true };
    }
    // 全角/半角カタカナと全半角長音記号のみを許可
    const katakanaRegex = /^[ァ-ヶヷ-ヺーｰｦ-ﾟ]+$/u;
    const valid = katakanaRegex.test(value);
    return valid
      ? { valid: true }
      : { valid: false, error: MessageCodeEnum.KANA_ONLY };
  },

  /**
   * 氏名の記号チェック
   *
   * @param value 値
   * @returns バリデーション結果
   */
  checkNameWithoutSymbols: (value: string): ValidationResult<typeof MessageCodeEnum.SYMBOL_NOT_ALLOWED> => {
    if (value === '') {
      // 未入力の場合はチェックしない
      return { valid: true };
    }
    // Unicodeの句読点・記号カテゴリは不可（スペースと長音記号は許可）
    const invalidCharacterRegex = /[\p{P}\p{S}]/u;
    const valid = !invalidCharacterRegex.test(value);
    return valid
      ? { valid: true }
      : { valid: false, error: MessageCodeEnum.SYMBOL_NOT_ALLOWED };
  },

  /**
   * 全角文字のみのチェック
   *
   * @param value 値
   * @returns バリデーション結果
   */
  checkFullWidthCharactersOnly: (value: string): ValidationResult<typeof MessageCodeEnum.FULL_WIDTH_CHARACTERS_ONLY> => {
    if (value === '') {
      // 未入力の場合はチェックしない
      return { valid: true };
    }
    // 半角文字（ASCII + 半角カナ）を除いた文字のみを許可
    const fullWidthCharactersRegex = /^[^\u0020-\u007E\uFF61-\uFF9F]+$/;
    const valid = fullWidthCharactersRegex.test(value);
    return valid
      ? { valid: true }
      : { valid: false, error: MessageCodeEnum.FULL_WIDTH_CHARACTERS_ONLY };
  },

  /**
   * 半角数値のみのチェック
   *
   * @param value 値
   * @returns バリデーション結果
   */
  checkNumericOnly: (value: string): ValidationResult<typeof MessageCodeEnum.NUMERIC_ONLY> => {
    if (value === '') {
      // 未入力の場合はチェックしない
      return { valid: true };
    }
    // 半角数値のみを許可
    const numericRegex = /^[0-9]+$/;
    const valid = numericRegex.test(value);
    return valid
      ? { valid: true }
      : { valid: false, error: MessageCodeEnum.NUMERIC_ONLY };
  },

  /**
   * 半角英数字のみのチェック
   *
   * @param value 値
   * @returns バリデーション結果
   */
  checkHalfWidthAlphanumeric: (value: string): ValidationResult<typeof MessageCodeEnum.HALF_WIDTH_ALPHANUMERIC_ONLY> => {
    if (value === '') {
      // 未入力の場合はチェックしない
      return { valid: true };
    }
    // 半角英数字のみを許可
    const halfWidthRegex = /^[a-zA-Z0-9]+$/;
    const valid = halfWidthRegex.test(value);
    return valid
      ? { valid: true }
      : { valid: false, error: MessageCodeEnum.HALF_WIDTH_ALPHANUMERIC_ONLY };
  },

  /**
   * 半角英数記号のみのチェック
   *
   * @param value 値
   * @returns バリデーション結果
   */
  checkHalfWidthAlphanumericOnly: (value: string): ValidationResult<typeof MessageCodeEnum.HALF_WIDTH_ALPHANUMERIC_SYMBOLS_ONLY> => {
    if (value === '') {
      // 未入力の場合はチェックしない
      return { valid: true };
    }
    // 半角英数記号のみを許可（ASCII文字範囲内のみ）
    // 主にメールアドレス用途のため、スペースは不可
    const halfWidthRegex = /^[a-zA-Z0-9!@#$%^&*()_+\-=[\]{};:'",.<>?/\\|`~]+$/;
    const valid = halfWidthRegex.test(value);
    return valid
      ? { valid: true }
      : { valid: false, error: MessageCodeEnum.HALF_WIDTH_ALPHANUMERIC_SYMBOLS_ONLY };
  },

  /**
   * メールアドレス形式チェック
   *
   * 半角英数記号で構成され、かつ「@」を含むことを確認する。
   *
   * @param value 値
   * @returns バリデーション結果
   */
  checkEmail: (
    value: string,
  ): ValidationResult<typeof MessageCodeEnum.HALF_WIDTH_ALPHANUMERIC_SYMBOLS_ONLY | typeof MessageCodeEnum.EMAIL_FORMAT_ONLY> => {
    if (value === '') {
      // 未入力の場合はチェックしない
      return { valid: true };
    }

    const halfWidthCheckResult = validationUtils.checkHalfWidthAlphanumericOnly(value);
    if (!halfWidthCheckResult.valid) {
      return halfWidthCheckResult;
    }

    const valid = value.includes('@');
    return valid
      ? { valid: true }
      : { valid: false, error: MessageCodeEnum.EMAIL_FORMAT_ONLY };
  },

  /**
   * 指定桁数チェック
   *
   * @param value 値
   * @param requiredLength 指定桁数
   * @returns バリデーション結果
   */
  checkSpecifiedLength: (
    value: string,
    requiredLength: number,
  ): ValidationResult<typeof MessageCodeEnum.SPECIFIED_LENGTH> => {
    if (value === '') {
      // 未入力の場合はチェックしない
      return { valid: true };
    }

    const valid = value.length === requiredLength;
    return valid
      ? { valid: true }
      : { valid: false, error: MessageCodeEnum.SPECIFIED_LENGTH };
  },
};
